import { FinancingApi } from '@servicetitan/financing-api-client';
import { useDependencies } from '@servicetitan/react-ioc';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

interface UseLegalDisclosureOptions {
    providerName: string | undefined;
    tenantId: string | undefined;
    category: string | string[] | undefined;
}

const DISCLOSURES_STALE_TIME = 5 * 60 * 1000;

export const useLegalDisclosure = (options: UseLegalDisclosureOptions) => {
    const { providerName, tenantId, category } = options;
    const [apiClient] = useDependencies(FinancingApi);
    const { data, isSuccess } = useQuery({
        queryKey: ['legalDisclosuresGetLegalDisclosure', providerName, tenantId],
        queryFn: () =>
            apiClient.legalDisclosuresGetLegalDisclosures({
                providerName: providerName!,
                tenantId: tenantId!,
            }),
        enabled: tenantId !== undefined && providerName !== undefined && category != null,
        staleTime: DISCLOSURES_STALE_TIME,
    });

    return useMemo(() => {
        if (category == null) {
            return '';
        }

        if (isSuccess && data == null) {
            return Array.isArray(category) ? [] : '';
        }

        const disclosures = data?.data?.disclosures;
        if (disclosures == null || disclosures.length === 0) {
            return Array.isArray(category) ? [] : '';
        }

        if (Array.isArray(category)) {
            return category
                .map(cat => {
                    const entry = disclosures.find(
                        ({ category: c }) => c?.toLowerCase() === cat.toLowerCase(),
                    );
                    return entry?.text ?? '';
                })
                .filter(text => text !== '');
        }

        const disclosureEntry = disclosures.find(
            ({ category: c }) => c?.toLowerCase() === category?.toLowerCase(),
        );

        return disclosureEntry?.text ?? '';
    }, [data, isSuccess, category]);
};
